
import { useState, useEffect } from "react";
import { GuessHistory, SavedGameData, GameStatus } from "@/lib/types/GameTypes";
import { loadGameState } from "./GamePersistenceV2";
import { getCurrentGameStatus, subscribeToGameStatus } from "./managers/GameStatusManager";
import { determineOverlayState } from "./managers/OverlayManager";

// Utility function to validate game status
const validateGameStatus = (status: any): GameStatus => {
  const validStatuses: GameStatus[] = ['playing', 'won', 'lost'];
  return validStatuses.includes(status as GameStatus) ? status as GameStatus : 'playing';
};

export const useGameState = () => {
  const [savedState, setSavedState] = useState<SavedGameData | null>(null);
  const [guesses, setGuesses] = useState<{ letter: string; status: string }[][]>([]);
  const [currentGuess, setCurrentGuess] = useState("");
  const [keyboardStatus, setKeyboardStatus] = useState<{ [key: string]: "correct" | "wrong-position" | "incorrect" | "empty" }>({});
  const [isValidating, setIsValidating] = useState(false);
  const [previousWord, setPreviousWord] = useState<{ word: string; guess: { letter: string; status: string }[] } | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [levelAttempts, setLevelAttempts] = useState<number[]>([]);
  const [showGameCompletion, setShowGameCompletion] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [gameStatus, setGameStatus] = useState<GameStatus>('playing');
  const [guessHistory, setGuessHistory] = useState<GuessHistory>([]);
  const [invalidGuessType, setInvalidGuessType] = useState<"duplicate" | "invalid" | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize state from saved data
  useEffect(() => {
    const initializeState = async () => {
      try {
        setIsLoading(true);
        // Force refresh state to ensure we have the latest data
        const state = await loadGameState(true);
        const initialGameStatus = await getCurrentGameStatus();
        const overlayState = determineOverlayState();
        
        if (state) {
          console.log('GameState - Initializing from saved data:', {
            guessCount: state.guesses?.length,
            guessHistoryCount: state.guessHistory?.length || 0,
            word: state.currentWord,
            level: state.currentLevel,
            status: initialGameStatus.status,
            showCompletion: initialGameStatus.showCompletion,
            overlayState,
            timestamp: new Date().toISOString()
          });
          
          setSavedState(state);
          
          // Set the guessHistory first, as we might need it to find level-specific guesses
          if (state.guessHistory) {
            // Deep clone to avoid reference issues
            setGuessHistory(JSON.parse(JSON.stringify(state.guessHistory)));
            console.log('Loaded guess history with entries:', {
              count: state.guessHistory.length,
              entries: state.guessHistory.map(entry => ({
                level: entry.level, 
                word: entry.word,
                guessCount: entry.guesses?.length || 0
              }))
            });
          }
          
          // Find the current level's guesses in the history if available
          if (state.currentWord) {
            const currentLevel = state.currentLevel;
            const currentLevelHistory = state.guessHistory?.find(
              h => h.level === currentLevel && h.word === state.currentWord
            );
            
            if (currentLevelHistory && currentLevelHistory.guesses?.length > 0) {
              console.log('GameState - Found level-specific guesses in history:', {
                level: currentLevel,
                word: state.currentWord,
                guessCount: currentLevelHistory.guesses.length,
                guessDetails: currentLevelHistory.guesses.map(g => g.map(l => l.letter).join('')),
                timestamp: new Date().toISOString()
              });
              
              // Deep clone to avoid reference issues
              const deepClonedGuesses = JSON.parse(JSON.stringify(currentLevelHistory.guesses));
              setGuesses(deepClonedGuesses);
              console.log('Set guesses from level history:', {
                guessCount: deepClonedGuesses.length,
                timestamp: new Date().toISOString()
              });
            } else {
              console.log('GameState - Using generic guesses from state:', {
                guessCount: state.guesses?.length || 0,
                guessDetails: state.guesses?.map(g => g.map(l => l.letter).join('')),
                timestamp: new Date().toISOString()
              });
              
              // Deep clone to avoid reference issues
              if (state.guesses && state.guesses.length > 0) {
                const deepClonedGuesses = JSON.parse(JSON.stringify(state.guesses));
                setGuesses(deepClonedGuesses);
              } else {
                setGuesses([]);
              }
            }
          } else {
            if (state.guesses && state.guesses.length > 0) {
              const deepClonedGuesses = JSON.parse(JSON.stringify(state.guesses));
              setGuesses(deepClonedGuesses);
            } else {
              setGuesses([]);
            }
          }
          
          setKeyboardStatus(state.keyboardStatus || {});
          setPreviousWord(state.previousWord || null);
          setLevelAttempts(state.levelAttempts || []);
          setGameStatus(validateGameStatus(initialGameStatus.status));
          setShowGameCompletion(initialGameStatus.showCompletion);
        }
      } catch (error) {
        console.error('Error loading game state:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeState();
  }, []);

  // Listen for game status changes
  useEffect(() => {
    const unsubscribe = subscribeToGameStatus((newState) => {
      try {
        const page = typeof window !== 'undefined' ? window.location.pathname : 'server';
        const mode = typeof window !== 'undefined' ? (localStorage.getItem('babblegram_game_mode') || 'daily') : 'unknown';
        console.log('📝 STATUS_EVENT - subscription', { page, mode, to: newState.status, showCompletion: newState.showCompletion, timestamp: new Date().toISOString() });
      } catch {}
      setGameStatus(newState.status);
      if (newState.status === 'won' || newState.status === 'lost') {
        setShowGameCompletion(true);
      }
    });
    
    return () => unsubscribe();
  }, []);
  
  // Listen for mode switch events
  useEffect(() => {
    const handleModeSwitch = async (event: CustomEvent) => {
      const { targetMode } = event.detail;
      console.log(`Mode switch event detected in GameState, reinitializing for ${targetMode} mode`);
      
      // Initialize immediately, no timeout needed
      initializeState();
    };
    
    const initializeState = async () => {
      try {
        setIsLoading(true);
        // Force refresh state to ensure we have the latest data for the current mode
        const state = await loadGameState(true);
        const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
        const initialGameStatus = await getCurrentGameStatus();
        
        console.log('GameState - Reinitializing after mode switch:', {
          currentMode,
          hasState: !!state,
          guessCount: state?.guesses?.length,
          guessHistoryCount: state?.guessHistory?.length || 0,
          word: state?.currentWord,
          level: state?.currentLevel,
          gameStatus: initialGameStatus.status,
          showCompletion: initialGameStatus.showCompletion,
          timestamp: new Date().toISOString()
        });
        
        if (state) {
          setSavedState(state);
          
          // Process game state as before
          if (state.guessHistory) {
            setGuessHistory(JSON.parse(JSON.stringify(state.guessHistory)));
          }
          
          // Find current level guesses
          if (state.currentWord) {
            const currentLevel = state.currentLevel;
            const currentLevelHistory = state.guessHistory?.find(
              h => h.level === currentLevel && h.word === state.currentWord
            );
            
            if (currentLevelHistory && currentLevelHistory.guesses?.length > 0) {
              const deepClonedGuesses = JSON.parse(JSON.stringify(currentLevelHistory.guesses));
              setGuesses(deepClonedGuesses);
            } else if (state.guesses && state.guesses.length > 0) {
              const deepClonedGuesses = JSON.parse(JSON.stringify(state.guesses));
              setGuesses(deepClonedGuesses);
            } else {
              setGuesses([]);
            }
          } else {
            setGuesses([]);
          }
          
          setKeyboardStatus(state.keyboardStatus || {});
          setPreviousWord(state.previousWord || null);
          setLevelAttempts(state.levelAttempts || []);
          setGameStatus(validateGameStatus(initialGameStatus.status));
          setShowGameCompletion(initialGameStatus.showCompletion);
        } else {
          // No state found for this mode - reset to defaults
          setSavedState(null);
          setGuesses([]);
          setGuessHistory([]);
          setKeyboardStatus({});
          setPreviousWord(null);
          setLevelAttempts([]);
          setGameStatus(validateGameStatus(initialGameStatus.status));
          setShowGameCompletion(initialGameStatus.showCompletion);
        }
      } catch (error) {
        console.error('Error reinitializing game state after mode switch:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    window.addEventListener('babblegram_mode_switched', handleModeSwitch);
    
    return () => {
      window.removeEventListener('babblegram_mode_switched', handleModeSwitch);
    };
  }, []);

  // Log when guesses state changes
  useEffect(() => {
    console.log('GameState - Guesses state updated:', {
      guessCount: guesses.length,
      guessDetails: guesses.map(g => g.map(l => l.letter).join('')),
      timestamp: new Date().toISOString()
    });
  }, [guesses]);

  return {
    guesses,
    setGuesses,
    currentGuess,
    setCurrentGuess,
    keyboardStatus,
    setKeyboardStatus,
    isValidating,
    setIsValidating,
    previousWord,
    setPreviousWord,
    isTransitioning,
    setIsTransitioning,
    levelAttempts,
    setLevelAttempts,
    showGameCompletion,
    setShowGameCompletion,
    showToast,
    setShowToast,
    gameStatus,
    setGameStatus,
    guessHistory,
    setGuessHistory,
    invalidGuessType,
    setInvalidGuessType,
    isLoading,
  };
};
